import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { loadCalculationMethods } from '../actionCreators';
import CommissionComponent from './CommissionComponent';
import { loadDealers, loadCommissionTypes } from 'actions/actionCreators/dictionariesActions';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { TableCell, TableRow } from '@mui/material';
import debounce from 'lodash.debounce';
import { useSelector, useDispatch } from 'react-redux';
const DEBOUNCE_DELAY = 750;

const selectCommissionTypes = (state, subsidyId) => {
  const loaded = state.dictionaries.commissionTypes.isLoaded;
  if (!loaded) return [];

  if (subsidyId) return state.dictionaries.commissionTypes.items.filter(item => item.isSubsidy);
  return state.dictionaries.commissionTypes.items.filter(item => item.isCommission);
};

const getCommissionTypes = isSubsidy => state => selectCommissionTypes(state, isSubsidy);

const CommissionContainer = ({
  index,
  commission,
  onPropertyChange,
  onRemove,
  validationInfo,
  cardName,
  isSubsidy,
  visibleColumns,
  editRight,
  addOrDeleteRight,
  calcInProgress
}) => {
  const dispatch = useDispatch();
  const commissionTypes = useSelector(state => getCommissionTypes(isSubsidy)(state));

  const loadCalculationMethodsInternal = useCallback(() => {
    if (!commission.commissionId) return Promise.resolve([]);
    return dispatch(loadCalculationMethods(commission.commissionId)).then(methods => methods || []);
  }, [commission.commissionId, dispatch]);

  const loadDealersInternal = useCallback(
    input => {
      if (!input) return Promise.resolve([]);
      return dispatch(loadDealers(input)).then(data => data);
    },
    [dispatch]
  );

  const loadDealersInternalDebounced = debounce(loadDealersInternal, DEBOUNCE_DELAY, { leading: true });
  const handlePropertyChange = useCallback(prop => value => onPropertyChange(index, prop, value), [onPropertyChange, index]);

  const handleCommissionTypeChange = useCallback(
    async value => {
      handlePropertyChange('commissionId')(value);

      const methods = await dispatch(loadCalculationMethods(value));
      const defaultMethod = methods?.find(cm => cm.isDefault);

      if (!defaultMethod) {
        handlePropertyChange('calculationMethod')({ calculationMethodId: null, calculationMethodName: null, commissionId: value });
        return;
      }
      handlePropertyChange('calculationMethod')({
        calculationMethodId: defaultMethod.calculationMethodId,
        calculationMethodName: defaultMethod.calculationMethodName,
        commissionId: value
      });
    },
    [handlePropertyChange, dispatch]
  );

  const handleRemoveCommission = () => onRemove(index);
  const handleCommissionPercentChange = handlePropertyChange('commissionPercent');
  const handleCommissionAmountChange = handlePropertyChange('commissionAmount');

  const onPercentChangeDebounced = debounce(handleCommissionPercentChange, DEBOUNCE_DELAY);
  const onAmountChangeDebounced = debounce(handleCommissionAmountChange, DEBOUNCE_DELAY);

  useEffect(() => {
    dispatch(loadCommissionTypes());
  }, [dispatch]);

  return calcInProgress.inProgress && calcInProgress.index === index ? (
    <TableRow>
      <TableCell colSpan={6}>
        <WrappedFakeText lines={2} />
      </TableCell>
    </TableRow>
  ) : (
    <CommissionComponent
      commission={commission}
      commissionTypes={commissionTypes}
      loadCalculationMethods={loadCalculationMethodsInternal}
      loadDealers={loadDealersInternalDebounced}
      onRemove={onRemove}
      index={index}
      validationInfo={validationInfo}
      cardName={cardName}
      isSubsidy={isSubsidy}
      visibleColumns={visibleColumns}
      editRight={editRight}
      addOrDeleteRight={addOrDeleteRight}
      calcInProgress={calcInProgress}
      onPropertyChange={handlePropertyChange}
      onCommissionTypeChange={handleCommissionTypeChange}
      onRemoveCommission={handleRemoveCommission}
      onPercentChange={onPercentChangeDebounced}
      onAmountChange={onAmountChangeDebounced}
    />
  );
};

CommissionContainer.propTypes = {
  commission: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  validationInfo: PropTypes.object,
  index: PropTypes.number.isRequired,
  cardName: PropTypes.string.isRequired,
  isSubsidy: PropTypes.bool.isRequired,
  visibleColumns: PropTypes.array.isRequired,
  editRight: PropTypes.bool.isRequired,
  addOrDeleteRight: PropTypes.bool.isRequired,
  calcInProgress: PropTypes.bool.isRequired
};

export default CommissionContainer;
